import React, { Suspense, useEffect, useState } from 'react';
import { memberId } from '@trello/session-cookie';
import { useLazyComponent } from '@trello/use-lazy-component';
import { forceShowOnboardingOverlay } from 'app/src/components/AtlassianAccountOnboardingBanner/AutomaticOnboarding/AtlassianAutomaticMigrationBanner';
import { forceEmailHygieneQuery } from 'app/src/components/EmailHygieneWizard/EmailHygieneBanner';
import { forceMigrationQuery } from 'app/src/components/AtlassianAccountMigration/AtlassianAccountMigrationBanner';
import { useAtlassianAccountMigrationStage } from './useAtlassianAccountMigrationStage';
import { AtlassianAccountMigrationStage as Stage } from './types';

// Wraps all banners related to managing Atlassian Account migration stages,
// which are by design exclusive from each other.
export const AtlassianAccountMigrationStageBanners = () => {
  const AtlassianAccountOnboardingBanner = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "atlassian-account-onboarding-banner" */ 'app/src/components/AtlassianAccountOnboardingBanner'
      ),
    { namedImport: 'AtlassianAccountOnboardingBanner' },
  );
  const AtlassianManagedAccountBanner = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "atlassian-account-onboarding-banner" */ 'app/src/components/AtlassianAccountOnboardingBanner'
      ),
    { namedImport: 'AtlassianManagedAccountBanner' },
  );
  const AtlassianAccountMigrationBanner = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "atlassian-account-migration-banner" */ 'app/src/components/AtlassianAccountMigration'
      ),
    { namedImport: 'AtlassianAccountMigrationBanner' },
  );
  const EmailHygieneBanner = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "email-hygiene-banner" */ 'app/src/components/EmailHygieneWizard'
      ),
    { namedImport: 'EmailHygieneBanner' },
  );
  const AtlassianAccountMigrationPrompt = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "atlassian-account-migration-banner" */ 'app/src/components/AtlassianAccountMigrationPrompt'
      ),
    { namedImport: 'AtlassianAccountMigrationPrompt' },
  );

  const showMigrationPrompt = (search: string) =>
    new URLSearchParams(search).get('showAaMigrate') || false;

  const { stage, me } = useAtlassianAccountMigrationStage({
    skip: !memberId,
  });
  const [forceStage, setForceStage] = useState<Stage | null>(null);
  const [showPrompt, setShowPrompt] = useState('');

  // If forcing open a banner with a query parameter, override actual stage.
  useEffect(() => {
    const query = window.location.search;
    if (forceShowOnboardingOverlay(me?.oneTimeMessagesDismissed ?? [])) {
      setForceStage(Stage.onboarding);
    } else if (forceMigrationQuery.test(query)) {
      setForceStage(Stage.migration);
    } else if (forceEmailHygieneQuery.test(query)) {
      setForceStage(Stage.emailHygiene);
    }
  }, [me]);

  useEffect(() => {
    const prompt = showMigrationPrompt(window.location.search) || '';
    if (prompt) {
      setShowPrompt(prompt);
    }
  }, []);

  const effectiveStage = forceStage || stage;

  if (!effectiveStage && !showPrompt) {
    return null;
  }

  const renderBanner = () => {
    switch (effectiveStage) {
      case Stage.onboarding:
      case Stage.legacyOnboardingV2:
        return (
          <AtlassianAccountOnboardingBanner
            isAaMastered={me?.isAaMastered ?? false}
          />
        );
      case Stage.newlyManaged:
        return <AtlassianManagedAccountBanner />;
      case Stage.migration:
        return <AtlassianAccountMigrationBanner forceShow={!!forceStage} />;
      case Stage.emailHygiene:
        return <EmailHygieneBanner forceShow={!!forceStage} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Suspense fallback={null}>{renderBanner()}</Suspense>
      <Suspense fallback={null}>
        {showPrompt && <AtlassianAccountMigrationPrompt prompt={showPrompt} />}
      </Suspense>
    </>
  );
};
